import type { DeviceSource, MappedAsset, MappableAsset } from "./types"

export interface FilteredSource extends DeviceSource {
  disabled: boolean
  mappedToAsset: string | null
}

/**
 * Filters device sources by asset class compatibility and marks already-mapped sources as disabled.
 *
 * - When assetClass is null, all sources are returned (no class filtering).
 * - Sources whose ref matches an existing mapping are marked disabled with
 *   a label indicating which asset they're already mapped to.
 */
/**
 * Whether something offering these classes can serve this asset.
 *
 * The single place the comparison is made. Case-insensitive because the two
 * sides disagree: an asset's class arrives from GraphQL as an enum ("BATTERY")
 * while compatible classes are raw values ("battery"). An asset with no class
 * set matches everything, since hiding all options would leave no way forward.
 *
 * Used for both discovered sources and connectors — they carry different
 * shapes but the same compatibleClasses semantics.
 */
export function matchesAssetClass(
  compatibleClasses: string[] | null | undefined,
  assetClass: string | null | undefined,
): boolean {
  if (!assetClass) return true
  const target = assetClass.toLowerCase()
  return !!compatibleClasses?.some(c => c.toLowerCase() === target)
}

/**
 * Whether this source can serve this asset.
 *
 * Core decides on class, Subtype and role and lists the fitting Assets in
 * `fittingAssetIds`; that is the only way to tell an AC Load's source from a DC
 * Load's, since both are class "component". When core sent no list (the request
 * named no Assets), fall back to the class.
 */
export function sourceFitsAsset(
  source: Pick<DeviceSource, "compatibleClasses" | "fittingAssetIds">,
  asset: { id: string; assetClass: string | null | undefined },
): boolean {
  if (source.fittingAssetIds != null) return source.fittingAssetIds.includes(asset.id)
  return matchesAssetClass(source.compatibleClasses, asset.assetClass)
}

/**
 * Sources this class of asset can be bound to.
 *
 * Compared case-insensitively because the two sides disagree on casing: an
 * asset's class arrives from GraphQL as an enum ("BATTERY") while
 * compatibleClasses are raw values ("battery"). An asset with no class set is
 * not filtered, since hiding everything would leave no way forward.
 *
 * `existingMappings` marks sources already bound, so callers can disable rather
 * than hide them. Pass [] when the caller has no mappings to consider.
 *
 * Given the asset's id, the sources are narrowed by `sourceFitsAsset` instead.
 */
export function filterSourcesByAssetClass(
  sources: DeviceSource[],
  assetClass: string | null | undefined,
  existingMappings: MappedAsset[],
  assetId?: string,
): FilteredSource[] {
  const mappedRefs = new Set(existingMappings.map(m => m.sourceRef))

  return sources
    .filter(source =>
      assetId
        ? sourceFitsAsset(source, { id: assetId, assetClass })
        : matchesAssetClass(source.compatibleClasses, assetClass),
    )
    .map(source => ({
      ...source,
      disabled: mappedRefs.has(source.ref),
      mappedToAsset: existingMappings.find(m => m.sourceRef === source.ref)?.assetName ?? null,
    }))
}

/**
 * Filters system assets by a source's compatible classes.
 *
 * - When compatibleClasses is empty, all assets are returned (no filtering).
 * - Only assets whose class matches one of the source's compatible classes are included.
 * - Assets with a null class are always included (cannot be filtered out).
 * - Given the source's `fittingAssetIds`, only the Assets it lists are included.
 */
export function filterAssetsBySourceClass(
  assets: MappableAsset[],
  compatibleClasses: string[],
  fittingAssetIds?: string[] | null,
): MappableAsset[] {
  if (fittingAssetIds != null) return assets.filter(asset => fittingAssetIds.includes(asset.id))
  if (compatibleClasses.length === 0) return assets
  return assets.filter(asset =>
    asset.assetClass === null ||
    compatibleClasses.some(c => c.toLowerCase() === asset.assetClass!.toLowerCase()),
  )
}
